// src/components/admin/employees/wizard/WorkDetails.js
import React, { useState, useEffect } from 'react';
import { Plus, X } from 'lucide-react';
import holidayPlanService from '../../../../services/holidayPlan.service';
import leavePlanService from '../../../../services/leavePlan.service';
import shiftService from '../../../../services/shift.service';
import { countries } from '../../../../data/countries';
const WorkDetails = ({ formData, updateFormData, theme }) => {

    const [showAddShift, setShowAddShift] = useState(false);
    const [customShifts, setCustomShifts] = useState([]);
    const [newShiftName, setNewShiftName] = useState('');
    const [newShiftFrom, setNewShiftFrom] = useState('09:00');
    const [newShiftTo, setNewShiftTo] = useState('18:00');
    const [showManageShifts, setShowManageShifts] = useState(false);

    // Missing in original: local state to store plans
    const [holidayPlans, setHolidayPlans] = useState([]);
    const [leavePlans, setLeavePlans] = useState([]);
    const [showAddLeavePlan, setShowAddLeavePlan] = useState(false);
    const [newLeavePlanName, setNewLeavePlanName] = useState('');
    const [creatingLeavePlan, setCreatingLeavePlan] = useState(false);
    const [leavePlanError, setLeavePlanError] = useState('');
    const [showAddHolidayPlan, setShowAddHolidayPlan] = useState(false);
    const [newHolidayPlanName, setNewHolidayPlanName] = useState('');
    const [newHolidayCountryCode, setNewHolidayCountryCode] = useState('IN');
    const [newHolidayYear, setNewHolidayYear] = useState(new Date().getFullYear());
    const [creatingHolidayPlan, setCreatingHolidayPlan] = useState(false);
    const [holidayPlanError, setHolidayPlanError] = useState('');

    // Load holiday plans, leave plans, and shifts from backend
    useEffect(() => {
        fetchShifts();
        fetchHolidayPlans();
        fetchLeavePlans();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchHolidayPlans = async () => {
        try {
            const plans = await holidayPlanService.getHolidayPlans();
            setHolidayPlans(plans || []);
        } catch (error) {
            console.error('Error loading holiday plans:', error);
            setHolidayPlans([]);
        }
    };

    const fetchLeavePlans = async () => {
        try {
            const plans = await leavePlanService.getLeavePlans();
            setLeavePlans(plans || []);
        } catch (error) {
            console.error('Error loading leave plans:', error);
            setLeavePlans([]);
        }
    };

    const fetchShifts = async () => {
        try {
            const shifts = await shiftService.getAllShifts();
            if (Array.isArray(shifts) && shifts.length > 0) {
                setCustomShifts(shifts);
            } else {
                setCustomShifts([]);
            }
        } catch (error) {
            console.error('Error loading shifts:', error);
            setCustomShifts([]);
        }
    };

    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;

        if (name === 'leavePlan' && value === '__add_leave_plan__') {
            setShowAddLeavePlan(true);
            return;
        }

        if (name === 'holidayList' && value === '__add_holiday_plan__') {
            setShowAddHolidayPlan(true);
            return;
        }

        if (name === 'shift_id' && value === '__add_shift__') {
            setShowAddShift(true);
            return;
        }

        updateFormData({ [name]: type === 'checkbox' ? checked : value });
    };

    const handleAddCustomShift = async () => {
        if (newShiftFrom && newShiftTo) {
            try {
                const shiftName = newShiftName.trim() || `Shift ${newShiftFrom} - ${newShiftTo}`;
                const shiftPayload = {
                    name: shiftName,
                    start_time: newShiftFrom,
                    end_time: newShiftTo,
                    grace_period: 15,
                    is_active: true
                };

                const createResponse = await shiftService.createShift(shiftPayload);
                const refreshedShifts = await shiftService.getAllShifts();
                const shiftList = Array.isArray(refreshedShifts) ? refreshedShifts : [];
                setCustomShifts(shiftList);

                let createdShiftId = createResponse?.id || createResponse?.shift?.id;
                if (Array.isArray(createResponse) && createResponse.length > 0) {
                    createdShiftId = createResponse[0]?.id;
                }
                if (!createdShiftId) {
                    const createdShift = shiftList.find((shift) => shift.name === shiftPayload.name);
                    createdShiftId = createdShift?.id;
                }
                if (createdShiftId) {
                    updateFormData({ shift_id: createdShiftId });
                }
                setNewShiftName('');
                setNewShiftFrom('09:00');
                setNewShiftTo('18:00');
                setShowAddShift(false);
            } catch (error) {
                console.error('Error adding shift:', error);
            }
        }
    };

    const handleAddLeavePlan = async () => {
        const planName = newLeavePlanName.trim();
        if (!planName) {
            setLeavePlanError('Enter leave plan name');
            return;
        }

        try {
            setCreatingLeavePlan(true);
            setLeavePlanError('');
            const planPayload = {
                plan_name: planName,
                description: 'Created from Add Employee Wizard',
                effective_from: new Date().toISOString().split('T')[0],
                leave_types: []
            };
            const createdPlan = await leavePlanService.createLeavePlan(planPayload);
            await fetchLeavePlans();
            if (createdPlan?.id) {
                updateFormData({ leavePlan: createdPlan.id });
            }
            setNewLeavePlanName('');
            setShowAddLeavePlan(false);
        } catch (error) {
            console.error('Error creating leave plan:', error);
            setLeavePlanError('Unable to create leave plan. Please try again.');
        } finally {
            setCreatingLeavePlan(false);
        }
    };

    const handleAddHolidayPlan = async () => {
        const planName = newHolidayPlanName.trim();
        if (!planName) {
            setHolidayPlanError('Enter holiday plan name');
            return;
        }

        try {
            setCreatingHolidayPlan(true);
            setHolidayPlanError('');
            const selectedCountry = countries.find((country) => country.code === newHolidayCountryCode) || countries[0];
            const fetchedHolidays = await holidayPlanService.fetchCountryHolidays(selectedCountry.code, Number(newHolidayYear));

            const planPayload = {
                name: planName,
                countryCode: selectedCountry.code,
                countryName: selectedCountry.name,
                year: Number(newHolidayYear),
                holidays: Array.isArray(fetchedHolidays) ? fetchedHolidays : []
            };

            const createdPlan = await holidayPlanService.createHolidayPlan(planPayload);
            await fetchHolidayPlans();
            if (createdPlan?.id) {
                updateFormData({ holidayList: createdPlan.id });
            }
            setNewHolidayPlanName('');
            setShowAddHolidayPlan(false);
        } catch (error) {
            console.error('Error creating holiday plan:', error);
            setHolidayPlanError('Unable to create holiday plan. Please try again.');
        } finally {
            setCreatingHolidayPlan(false);
        }
    };

    const handleDeleteShift = async (shiftId) => {
        try {
            await shiftService.deleteShift(shiftId);
            await fetchShifts();

            // Clear selection if deleted shift was selected
            const deletedShift = customShifts.find((s) => s.id === shiftId);
            if (deletedShift && formData.shift_id === deletedShift.id) {
                updateFormData({ shift_id: '' });
            }
        } catch (error) {
            console.error('Error deleting shift:', error);
        }
    };

    const inputClass = `w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 ${
        theme === 'dark'
            ? 'bg-gray-700 border-gray-600 text-white placeholder-gray-400'
            : 'bg-white border-gray-300 text-gray-900 placeholder-gray-400'
    }`;

    const selectClass = `w-full px-3 py-2 text-sm border rounded-md focus:outline-none focus:ring-2 focus:ring-green-500 max-h-10 ${
        theme === 'dark'
            ? 'bg-gray-700 border-gray-600 text-white'
            : 'bg-white border-gray-300 text-gray-900'
    }`;

    const labelClass = `block text-sm font-medium mb-1.5 ${
        theme === 'dark' ? 'text-gray-300' : 'text-gray-700'
    }`;

    const sectionTitleClass = `text-base font-semibold mb-3 ${
        theme === 'dark' ? 'text-white' : 'text-gray-900'
    }`;

    return (
        <div className="space-y-4">
            {/* Leave Settings */}
            <h3 className={sectionTitleClass}>Leave Settings</h3>

            <div className="grid grid-cols-2 gap-3">
                <div>
                    <label className={labelClass}>
                        Leave Plan<span className="text-red-500">*</span>
                    </label>
                    {!showAddLeavePlan ? (
                        <div className="relative">
                            <select
                                name="leavePlan"
                                value={formData.leavePlan || ''}
                                onChange={handleChange}
                                required
                                className={selectClass}
                                size="1"
                            >
                                <option value="">Select Leave Plan</option>
                                <option value="__add_leave_plan__">+ Add Leave Plan</option>
                                {leavePlans.map((plan) => (
                                    <option key={plan.id} value={plan.id}>
                                        {plan.plan_name}
                                    </option>
                                ))}
                            </select>
                            <button
                                type="button"
                                onClick={() => setShowAddLeavePlan(true)}
                                className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                                }`}
                                title="Add Leave Plan"
                            >
                                <Plus className="w-4 h-4" />
                            </button>
                            {leavePlans.length === 0 && (
                                <p className="text-xs text-orange-500 mt-1">
                                    No leave plans found. Create one here to continue.
                                </p>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <input
                                type="text"
                                value={newLeavePlanName}
                                onChange={(e) => {
                                    setNewLeavePlanName(e.target.value);
                                    if (leavePlanError) setLeavePlanError('');
                                }}
                                placeholder="Enter leave plan name"
                                className={inputClass}
                                autoFocus
                            />
                            {leavePlanError && <p className="text-xs text-red-500">{leavePlanError}</p>}
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={handleAddLeavePlan}
                                    disabled={creatingLeavePlan}
                                    className={`px-3 py-2 text-sm rounded-md ${
                                        creatingLeavePlan
                                            ? 'bg-gray-400 text-white cursor-not-allowed'
                                            : 'bg-green-600 text-white hover:bg-green-700'
                                    }`}
                                >
                                    {creatingLeavePlan ? 'Adding...' : 'Add'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowAddLeavePlan(false);
                                        setNewLeavePlanName('');
                                        setLeavePlanError('');
                                    }}
                                    className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
                <div>
                    <label className={labelClass}>
                        Holiday List<span className="text-red-500">*</span>
                    </label>
                    {!showAddHolidayPlan ? (
                        <div className="relative">
                            <select
                                name="holidayList"
                                value={formData.holidayList || ''}
                                onChange={handleChange}
                                required
                                className={selectClass}
                                size="1"
                            >
                                <option value="">Select Holiday List</option>
                                <option value="__add_holiday_plan__">+ Add Holiday Plan</option>
                                {holidayPlans.map((plan) => (
                                    <option key={plan.id} value={plan.id}>
                                        {plan.name} ({plan.country_name} {plan.year})
                                    </option>
                                ))}
                            </select>
                            <button
                                type="button"
                                onClick={() => setShowAddHolidayPlan(true)}
                                className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 ${
                                    theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                                }`}
                                title="Add Holiday Plan"
                            >
                                <Plus className="w-4 h-4" />
                            </button>
                            {holidayPlans.length === 0 && (
                                <p className="text-xs text-orange-500 mt-1">
                                    No holiday plans found. Create one here to continue.
                                </p>
                            )}
                        </div>
                    ) : (
                        <div className="space-y-2">
                            <input
                                type="text"
                                value={newHolidayPlanName}
                                onChange={(e) => {
                                    setNewHolidayPlanName(e.target.value);
                                    if (holidayPlanError) setHolidayPlanError('');
                                }}
                                placeholder="Enter holiday plan name"
                                className={inputClass}
                                autoFocus
                            />
                            <div className="grid grid-cols-2 gap-2">
                                <select
                                    value={newHolidayCountryCode}
                                    onChange={(e) => setNewHolidayCountryCode(e.target.value)}
                                    className={selectClass}
                                >
                                    {countries.map((country) => (
                                        <option key={country.code} value={country.code}>
                                            {country.flag} {country.name}
                                        </option>
                                    ))}
                                </select>
                                <input
                                    type="number"
                                    min="2000"
                                    max="2100"
                                    value={newHolidayYear}
                                    onChange={(e) => setNewHolidayYear(e.target.value)}
                                    className={inputClass}
                                />
                            </div>
                            {holidayPlanError && <p className="text-xs text-red-500">{holidayPlanError}</p>}
                            <div className="flex gap-2">
                                <button
                                    type="button"
                                    onClick={handleAddHolidayPlan}
                                    disabled={creatingHolidayPlan}
                                    className={`px-3 py-2 text-sm rounded-md ${
                                        creatingHolidayPlan
                                            ? 'bg-gray-400 text-white cursor-not-allowed'
                                            : 'bg-green-600 text-white hover:bg-green-700'
                                    }`}
                                >
                                    {creatingHolidayPlan ? 'Adding...' : 'Add'}
                                </button>
                                <button
                                    type="button"
                                    onClick={() => {
                                        setShowAddHolidayPlan(false);
                                        setNewHolidayPlanName('');
                                        setHolidayPlanError('');
                                    }}
                                    className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            </div>

            {/* Attendance Settings */}
            <div className="flex items-center justify-between mt-4">
                <h3 className={sectionTitleClass}>Attendance Settings</h3>
                <label className="relative inline-flex items-center cursor-pointer">
                    <input
                        type="checkbox"
                        name="attendanceTracking"
                        checked={!!formData.attendanceTracking}
                        onChange={handleChange}
                        className="sr-only peer"
                    />
                    <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-green-300 dark:peer-focus:ring-green-800 rounded-full peer dark:bg-gray-700 peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all dark:border-gray-600 peer-checked:bg-green-600"></div>
                    <span className={`ml-3 text-sm font-medium ${theme === 'dark' ? 'text-gray-300' : 'text-gray-900'}`}>
            Attendance tracking
          </span>
                </label>
            </div>

            {formData.attendanceTracking && (
                <>
                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <div className="flex items-center justify-between mb-1.5">
                                <label className={labelClass}>
                                    Shift<span className="text-red-500">*</span>
                                </label>
                                {customShifts.length > 0 && (
                                    <button
                                        type="button"
                                        onClick={() => setShowManageShifts((s) => !s)}
                                        className="text-xs text-blue-600 dark:text-blue-400 hover:underline"
                                    >
                                        {showManageShifts ? 'Hide' : 'Manage Custom Shifts'}
                                    </button>
                                )}
                            </div>

                            {!showAddShift ? (
                                <div className="relative">
                                    <select
                                        name="shift_id"
                                        value={formData.shift_id || ''}
                                        onChange={handleChange}
                                        required
                                        className={selectClass}
                                    >
                                        <option value="">Select Shift</option>
                                        <option value="__add_shift__">+ Add Shift</option>
                                        {customShifts.map((shift) => (
                                            <option key={shift.id} value={shift.id}>
                                                {shift.name} ({shift.start_time} - {shift.end_time})
                                            </option>
                                        ))}
                                    </select>
                                    <button
                                        type="button"
                                        onClick={() => setShowAddShift(true)}
                                        className={`absolute right-2 top-1/2 -translate-y-1/2 p-1 rounded hover:bg-gray-200 dark:hover:bg-gray-600 ${
                                            theme === 'dark' ? 'text-gray-300' : 'text-gray-600'
                                        }`}
                                        title="Add Custom Shift"
                                    >
                                        <Plus className="w-4 h-4" />
                                    </button>
                                </div>
                            ) : (
                                <div className="space-y-2">
                                    <input
                                        type="text"
                                        value={newShiftName}
                                        onChange={(e) => setNewShiftName(e.target.value)}
                                        placeholder="Shift Name (optional)"
                                        className={inputClass}
                                        autoFocus
                                    />
                                    <div className="grid grid-cols-2 gap-2">
                                        <div>
                                            <label className={labelClass}>From</label>
                                            <input
                                                type="time"
                                                value={newShiftFrom}
                                                onChange={(e) => setNewShiftFrom(e.target.value)}
                                                className={inputClass}
                                            />
                                        </div>
                                        <div>
                                            <label className={labelClass}>To</label>
                                            <input
                                                type="time"
                                                value={newShiftTo}
                                                onChange={(e) => setNewShiftTo(e.target.value)}
                                                className={inputClass}
                                            />
                                        </div>
                                    </div>
                                    <div className="flex gap-2">
                                        <button
                                            type="button"
                                            onClick={handleAddCustomShift}
                                            className="px-3 py-1.5 text-sm bg-green-600 text-white rounded-md hover:bg-green-700"
                                        >
                                            Add
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setShowAddShift(false);
                                                setNewShiftName('');
                                                setNewShiftFrom('09:00');
                                                setNewShiftTo('18:00');
                                            }}
                                            className="px-3 py-1.5 text-sm border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
                                        >
                                            Cancel
                                        </button>
                                    </div>
                                </div>
                            )}

                            {/* Manage Custom Shifts */}
                            {showManageShifts && customShifts.length > 0 && (
                                <div
                                    className={`mt-2 p-2 border rounded-md ${
                                        theme === 'dark' ? 'border-gray-600 bg-gray-700' : 'border-gray-300 bg-gray-50'
                                    }`}
                                >
                                    <p className="text-xs font-medium mb-2 text-gray-600 dark:text-gray-400">Custom Shifts:</p>
                                    <div className="space-y-1">
                                        {customShifts.map((shift) => (
                                            <div key={shift.id} className="flex items-center justify-between">
                                                <span className="text-xs text-gray-700 dark:text-gray-300">{shift.name}</span>
                                                <button
                                                    type="button"
                                                    onClick={() => handleDeleteShift(shift.id)}
                                                    className="p-1 text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded"
                                                    title="Delete shift"
                                                >
                                                    <X className="w-3 h-3" />
                                                </button>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        <div>
                            <label className={labelClass}>
                                Weekly Off<span className="text-red-500">*</span>
                            </label>
                            <select
                                name="weeklyOff"
                                value={formData.weeklyOff || 'Standard Weekly Off Policy'}
                                onChange={handleChange}
                                required
                                className={selectClass}
                                size="1"
                            >
                                <option value="Standard Weekly Off Policy">Standard Weekly Off Policy</option>
                                <option value="Saturday & Sunday">Saturday & Sunday</option>
                                <option value="Sunday Only">Sunday Only</option>
                                <option value="Custom">Custom</option>
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={labelClass}>Attendance Number</label>
                            <input
                                type="text"
                                name="attendanceNumber"
                                value={formData.attendanceNumber || ''}
                                onChange={handleChange}
                                placeholder="Attendance Number"
                                className={inputClass}
                            />
                        </div>
                        <div>
                            <label className={labelClass}>
                                Attendance Capture Scheme<span className="text-red-500">*</span>
                            </label>
                            <select
                                name="attendanceCaptureScheme"
                                value={formData.attendanceCaptureScheme || 'Biometric Capture Scheme'}
                                onChange={handleChange}
                                required
                                className={selectClass}
                                size="1"
                            >
                                <option value="Biometric Capture Scheme">Biometric Capture Scheme</option>
                                <option value="Manual Entry">Manual Entry</option>
                                <option value="Mobile App">Mobile App</option>
                                <option value="Web Portal">Web Portal</option>
                            </select>
                        </div>
                    </div>

                    <div className="grid grid-cols-2 gap-3">
                        <div>
                            <label className={labelClass}>Attendance Tracking Policy</label>
                            <select
                                name="attendanceTrackingPolicy"
                                value={formData.attendanceTrackingPolicy || ''}
                                onChange={handleChange}
                                className={selectClass}
                                size="1"
                            >
                                <option value="">Select Policy</option>
                                <option value="Standard Policy">Standard Policy</option>
                                <option value="Flexible Policy">Flexible Policy</option>
                                <option value="Strict Policy">Strict Policy</option>
                            </select>
                        </div>
                        <div>
                            <label className={labelClass}>Overtime</label>
                            <select
                                name="overtime"
                                value={formData.overtime || ''}
                                onChange={handleChange}
                                className={selectClass}
                                size="1"
                            >
                                <option value="">Select Overtime Policy</option>
                                <option value="Eligible">Eligible</option>
                                <option value="Not Eligible">Not Eligible</option>
                                <option value="As per rules">As per rules</option>
                            </select>
                        </div>
                    </div>
                </>
            )}
        </div>
    );
};

export default WorkDetails;
